import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { LoRaDevice, CalibrationChannel, CalibrationResponse } from '../types';
import { useSendCalibration } from '../hooks/useLoRaCalibration';

interface CalibrationDialogProps {
  device: LoRaDevice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CalibrationDialog({ device, open, onOpenChange }: CalibrationDialogProps) {
  const [channel, setChannel] = useState<CalibrationChannel>('temperature');
  const [enable, setEnable] = useState(true);
  const [offsetValue, setOffsetValue] = useState(0);
  const [result, setResult] = useState<CalibrationResponse | null>(null);

  const { mutateAsync, isPending } = useSendCalibration();

  const handleSubmit = async () => {
    if (!device) return;
    setResult(null);

    try {
      const response = await mutateAsync({
        devEui: device.devEui,
        channel,
        enable,
        offsetValue,
        wirelessDeviceId: device.wirelessDeviceId ?? undefined,
      });
      setResult(response);
    } catch {
      // Error toast is handled by the mutation hook's onError
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setResult(null);
      setChannel('temperature');
      setEnable(true);
      setOffsetValue(0);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Calibrate Sensor</DialogTitle>
          <DialogDescription>
            Send a remote calibration command to{' '}
            <span className="font-mono">{device?.devEui}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Channel selection */}
          <div className="space-y-2">
            <Label>Channel</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={channel === 'temperature' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannel('temperature')}
                disabled={isPending}
              >
                Temperature
              </Button>
              <Button
                type="button"
                variant={channel === 'humidity' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannel('humidity')}
                disabled={isPending}
              >
                Humidity
              </Button>
            </div>
          </div>

          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="cal-enable">Enable calibration</Label>
            <Switch
              id="cal-enable"
              checked={enable}
              onCheckedChange={setEnable}
              disabled={isPending}
            />
          </div>

          {/* Offset value */}
          <div className="space-y-2">
            <Label htmlFor="cal-offset">
              Offset ({channel === 'temperature' ? '\u00B0C' : '%RH'})
            </Label>
            <Input
              id="cal-offset"
              type="number"
              step="0.1"
              min="-10"
              max="10"
              value={offsetValue}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setOffsetValue(isNaN(val) ? 0 : val);
              }}
              disabled={isPending}
            />
            <p className="text-xs text-muted-foreground">
              Range: -10.0 to +10.0
            </p>
          </div>

          {/* Result display */}
          {result && (
            <div className={`rounded-md p-3 text-sm ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                {result.success ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <span className="font-medium">
                  {result.success ? 'Calibration sent' : 'Failed'}
                </span>
              </div>
              {result.payload && (
                <p className="text-muted-foreground">
                  Payload: <code className="font-mono">{result.payload}</code>
                </p>
              )}
              {result.messageId && (
                <p className="text-muted-foreground">
                  Message ID: <code className="font-mono text-xs">{result.messageId}</code>
                </p>
              )}
              {result.error && (
                <p className="text-red-600">{result.error}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Close
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send Calibration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
